/** As the inputlists API sends it (module `inputlists`; the web app does not import server modules). */
export const INPUTLISTS_RESOURCE = 'inputlists.lists';

export const inputlistKey = (eventId: string) => ['inputlist', eventId] as const;

export type Channel = {
  id: string;
  number: number;
  source: string;
  /** „Rider”: what the rider says, exactly (free text). `riderMic`: „Mikrofon / DI”, `riderStand`: „Statyw / uchwyt”. */
  riderMic: string;
  riderStand: string;
  /** „Dajemy”: a catalog item (its name; null: none, or gone from the catalog). `micItemId`: a mic OR a DI. */
  micItemId: string | null;
  micItemName: string | null;
  /** The „Mikrofon / DI” item is marked „Wymaga uchwytu” in the catalog. */
  micRequiresHolder: boolean;
  standItemId: string | null;
  standItemName: string | null;
  notes: string;
  version: number;
};

export type InputlistView = { channels: Channel[]; canSend: boolean };

export type PlanEntry = { itemId: string; name: string; quantity: number };
export type SendPlan = { add: PlanEntry[]; remove: PlanEntry[] };
/** What a send did; `notes`: what could not be taken back and why („Shure SM57: zdjęto 1× z 2× – reszta jest już załadowana”). */
export type SendResult = SendPlan & { notes: string[] };

/** The two kinds of gear of a channel (owner 2026-11-05): the Rider text and the „Dajemy” item of each. */
export const GEAR = [
  { key: 'mic', label: 'Mikrofon / DI', rider: 'riderMic', item: 'micItemId', name: 'micItemName' },
  { key: 'stand', label: 'Statyw / uchwyt', rider: 'riderStand', item: 'standItemId', name: 'standItemName' },
] as const;
export type Gear = (typeof GEAR)[number];

/** Lowercase, without spaces and dashes (the match rule of „zamiennik”, documented in the help page). */
const normalized = (text: string) => text.toLowerCase().replace(/[\s\-‐-―]+/g, '');

/**
 * „zamiennik”: the rider asks for something and the picked catalog item does not match it — the item's name does not
 * contain the rider's text, nor the rider's text the item's name (both `normalized`).
 */
export function isSubstitute(rider: string, itemName: string | null): boolean {
  const r = normalized(rider);
  if (r === '' || itemName === null) return false;
  const n = normalized(itemName);
  return !(n.includes(r) || r.includes(n));
}

/** The whole „Rider” group is hidden while every Rider cell of the list is empty. */
export const riderHidden = (channels: readonly Channel[]): boolean => channels.every((c) => GEAR.every((g) => c[g.rider].trim() === ''));

/** The lines of the „Wyślij do sprzętówki” dialog. */
export function sendPlanText(plan: SendPlan): string[] {
  const list = (entries: readonly PlanEntry[]) => entries.map((e) => `${e.quantity}× ${e.name}`).join(', ');
  const out: string[] = [];
  if (plan.add.length > 0) out.push(`Dodam: ${list(plan.add)}`);
  if (plan.remove.length > 0) out.push(`Zdejmę: ${list(plan.remove)}`);
  return out.length > 0 ? out : ['Nic do zmiany: sprzętówka ma już to, co daje inputlista'];
}

/** Most rows one drag down adds (as the server). */
export const MAX_FILL = 100;

/** Drag down: the rows passed by the pointer (`deltaY` px, rows `rowHeight` px), from half a row on; 0 … `MAX_FILL`. */
export function dragCount(deltaY: number, rowHeight: number): number {
  if (rowHeight <= 0 || deltaY <= 0) return 0;
  return Math.min(MAX_FILL, Math.round(deltaY / rowHeight));
}

/** The channels whose „Mikrofon / DI” needs a holder („Wymaga uchwytu”) and has no „Statyw / uchwyt”. */
export const missingHolders = (channels: readonly Channel[]): number[] => channels.filter((c) => c.micRequiresHolder && c.micItemId !== null && c.standItemId === null).map((c) => c.number);
